package com.dualremotetest

import kotlin.math.abs

// Phones, tablets and game controllers. Touch controls and controller sticks are turned into the same key events a
// remote sends (DPAD directions, a fire key, BACK), so the game itself only ever sees keys. Plain Kotlin (Android
// types only for constants) so it is unit-tested on the JVM.

/** The on-screen controls' own input channel. Real devices have ids ≥ 0 and injected events -1, so this never clashes. */
const val TOUCH_DEVICE_ID = -2

/** Controller buttons that fire (and select in menus), alongside the remote's OK, Enter and Space. */
val GAMEPAD_FIRE_KEYS = setOf(Keys.KEYCODE_BUTTON_A, Keys.KEYCODE_BUTTON_X)

fun isTouchChannel(ch: Channel?) = ch?.deviceId == TOUCH_DEVICE_ID

/** A key event from the on-screen controls, shaped like one from a remote. */
fun touchKey(keyCode: Int, down: Boolean, uptime: Long, wallTime: Long) = KeyInput(
    keyCode = keyCode,
    keyName = keyLabel(keyCode),
    action = if (down) Keys.ACTION_DOWN else Keys.ACTION_UP,
    repeatCount = 0,
    deviceId = TOUCH_DEVICE_ID,
    source = Devices.SOURCE_TOUCHSCREEN,
    flags = 0,
    scanCode = 0,
    eventTime = uptime,
    downTime = uptime,
    receivedUptime = uptime,
    wallTime = wallTime,
)

/**
 * The on-screen D-pad: the direction for a finger at ([dx], [dy]) from the pad's centre (screen axes, y down), or none
 * inside the dead zone. The larger of the two offsets wins, so diagonals pick one direction as a remote's D-pad does.
 */
fun touchPadDirection(dx: Float, dy: Float, radius: Float): Int? {
    if (dx * dx + dy * dy < (radius * TOUCH_DEAD_ZONE) * (radius * TOUCH_DEAD_ZONE)) return null
    return if (abs(dx) >= abs(dy)) {
        if (dx > 0) Keys.KEYCODE_DPAD_RIGHT else Keys.KEYCODE_DPAD_LEFT
    } else {
        if (dy > 0) Keys.KEYCODE_DPAD_DOWN else Keys.KEYCODE_DPAD_UP
    }
}

/** Share of the pad's radius around its centre that means "no direction". */
const val TOUCH_DEAD_ZONE = 0.25f

/**
 * What a controller's other buttons do: START pauses and resumes (BACK); B goes back in menus but not in play; the
 * rest do nothing. Left unhandled, Android would turn some of them (B, Y) into BACK and pause the game mid-fight.
 */
enum class PadButton { BACK, IGNORE, NONE }

fun padButton(keyCode: Int, phase: Phase): PadButton = when {
    keyCode in GAMEPAD_FIRE_KEYS -> PadButton.NONE
    keyCode == Keys.KEYCODE_BUTTON_START -> PadButton.BACK
    keyCode == Keys.KEYCODE_BUTTON_B -> if (phase == Phase.PLAYING) PadButton.IGNORE else PadButton.BACK
    isGamepadButton(keyCode) -> PadButton.IGNORE
    else -> PadButton.NONE
}

/** KEYCODE_BUTTON_A..MODE and BUTTON_1..16, as KeyEvent.isGamepadButton (which the JVM tests cannot call). */
fun isGamepadButton(keyCode: Int) =
    keyCode in Keys.KEYCODE_BUTTON_A..Keys.KEYCODE_BUTTON_MODE || keyCode in Keys.KEYCODE_BUTTON_1..Keys.KEYCODE_BUTTON_16

/**
 * Turns controller D-pads that report as hat axes, and left sticks, into DPAD key presses and releases, one held
 * direction per controller. The hat wins over the stick; on a stick the larger axis wins.
 */
class PadSticks {
    private val held = HashMap<Int, Int>()

    /** The key events for one joystick motion sample from [deviceId]; empty when the direction did not change. */
    fun update(deviceId: Int, source: Int, x: Float, y: Float, hatX: Float, hatY: Float, uptime: Long, wallTime: Long): List<KeyInput> {
        val want = direction(hatX, hatY, HAT_THRESHOLD) ?: direction(x, y, STICK_THRESHOLD)
        val was = held[deviceId]
        if (want == was) return emptyList()
        val out = ArrayList<KeyInput>(2)
        if (was != null) out += key(was, Keys.ACTION_UP, deviceId, source, uptime, wallTime)
        if (want != null) {
            held[deviceId] = want
            out += key(want, Keys.ACTION_DOWN, deviceId, source, uptime, wallTime)
        } else {
            held.remove(deviceId)
        }
        return out
    }

    /** Releases everything (for example when the window loses focus); the game clears its own held state too. */
    fun reset() = held.clear()

    private fun direction(x: Float, y: Float, threshold: Float): Int? = when {
        abs(x) < threshold && abs(y) < threshold -> null
        abs(x) >= abs(y) -> if (x > 0) Keys.KEYCODE_DPAD_RIGHT else Keys.KEYCODE_DPAD_LEFT
        else -> if (y > 0) Keys.KEYCODE_DPAD_DOWN else Keys.KEYCODE_DPAD_UP
    }

    private fun key(code: Int, action: Int, deviceId: Int, source: Int, uptime: Long, wallTime: Long) = KeyInput(
        keyCode = code,
        keyName = keyLabel(code) + " (stick)",
        action = action,
        repeatCount = 0,
        deviceId = deviceId,
        source = source,
        flags = 0,
        scanCode = 0,
        eventTime = uptime,
        downTime = uptime,
        receivedUptime = uptime,
        wallTime = wallTime,
    )

    companion object {
        const val HAT_THRESHOLD = 0.5f
        const val STICK_THRESHOLD = 0.5f
    }
}

/**
 * The whole-number scale for the 256×224 picture on a [width]×[height] screen, leaving at least [sideReserve] pixels
 * on each side for touch controls. Never below 1; with no room to spare the reserve is given up.
 */
fun pictureScale(width: Int, height: Int, sideReserve: Int): Int {
    val byHeight = height / PixelRenderer.H
    val withRoom = minOf(byHeight, (width - 2 * sideReserve) / PixelRenderer.W)
    return maxOf(1, if (withRoom >= 1) withRoom else minOf(byHeight, width / PixelRenderer.W))
}
